import { Fragment, useCallback, useEffect, useState } from 'react'
import { I18n } from 'aws-amplify'
import { useDispatch } from 'react-redux'

import HeaderV2 from '@/components/Operations/Shared/HeaderV2'
import OrganizationsFilter from '../../Shared/Filters/OrganizationsFilter'

import {
  Flex,
  Button,
  Cell,
  Text,
  Column,
  Row,
  Slot,
  Table,
  Loader
} from '@/primitives'

import { Pagination } from '@/elements'

import { requestPaymentTerms } from '@/actions/operations/paymentTerm'

import {
  getCurrentUser,
  getOperationsPaymentTerms,
  getOperationsPaymentTermsCount,
  getOperationsLoading
} from '@/reducers/selectors'

import history from '../../../../history'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_MANAGEMENT
} from '@/Util/PermissionUtils'

function PaymentTermTable({ state, dispatchState, modulePath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const paymentTerms = getOperationsPaymentTerms()
  const paymentTermsCount = getOperationsPaymentTermsCount()
  const loading = getOperationsLoading()

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)
  const [fetchCalled, setFetchCalled] = useState(false)

  const fetchPaymentTerms = useCallback(
    page => {
      const { organizations } = state
      if (organizations.length > 0) {
        dispatch(
          requestPaymentTerms({
            filter: {
              organizationId: organizations
            },
            limit: pageSize,
            offset: page - 1
          })
        )
      }
      setFetchCalled(true)
    },
    [state, pageSize, dispatch]
  )

  const changePage = useCallback(
    newPage => {
      dispatchState({ page: newPage })
      setFetchCalled(false)
    },
    [dispatchState]
  )

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_MANAGEMENT
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.page !== state.page
    ) {
      if (!fetchCalled) {
        fetchPaymentTerms(state.page)
      }
    }
  }, [state, tableState, fetchPaymentTerms, fetchCalled])

  useEffect(() => {
    setTableState({ ...state })
  }, [state])

  const onCreate = () => history.push(`${modulePath}/create`)

  const onEdit = id => history.push(`${modulePath}/update/${id}`)

  const onPreview = async e => {
    window.open(e.currentTarget.id)
  }

  return (
    <Fragment>
      <HeaderV2
        title={I18n.get('Manage Payment Terms')}
        buttonText={I18n.get('Add Payment Terms')}
        buttonCallback={onCreate}
        buttonIcon={'add'}
        showButton={canEdit}
      >
        <OrganizationsFilter state={state} dispatchState={dispatchState} />
      </HeaderV2>
      <Loader isLoading={loading}>
        {paymentTermsCount === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{I18n.get('No entries')}</Text>
          </Flex>
        )}
        {paymentTermsCount > 0 && (
          <Table aria-colcount='2' className='Operations__Table'>
            <Slot name='head'>
              <Row>
                <Column>{I18n.get('Name')}</Column>
                <Column>{I18n.get('Term Sheet')}</Column>
                <Column></Column>
              </Row>
            </Slot>
            <Slot name='body'>
              {paymentTerms.map(({ id, name, termSheetDocument }) => (
                <Row key={id}>
                  <Cell columnName={I18n.get('Name')}>{name}</Cell>
                  <Cell columnName={I18n.get('Term Sheet')}>
                    {termSheetDocument?.url ? (
                      <Button
                        size='small'
                        iconBefore='visibility'
                        id={termSheetDocument.url}
                        onClick={onPreview}
                      />
                    ) : (
                      <Text tone={900} variant='page'>
                        {I18n.get('No term sheet')}
                      </Text>
                    )}
                  </Cell>
                  <Cell className='Operations__Table__Column__Actions'>
                    {canEdit && (
                      <Button
                        variant='info'
                        size='small'
                        iconBefore='edit'
                        onClick={() => onEdit(id)}
                      />
                    )}
                  </Cell>
                </Row>
              ))}
            </Slot>
          </Table>
        )}
        {paymentTermsCount > pageSize && (
          <Pagination
            type='simple'
            totalItems={paymentTermsCount}
            totalPages={totalPages}
            setTotalPages={setTotalPages}
            page={tableState.page}
            setPage={changePage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      </Loader>
    </Fragment>
  )
}

export default PaymentTermTable
